import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  LinearProgress,
  Typography,
  Box,
  Card,
  CardActions,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { Delete, CheckCircle, CloudUpload, PhotoCamera } from '@mui/icons-material';
import { storage } from '../../../firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, listAll } from "firebase/storage";
import optimizeAndConvertToWebP from '../../../utils/optimizeAndConvertToWebP';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useAuth } from '../../../context/AuthContext';

const ImageManagerModal = ({
  open,
  onClose,
  onSelectImage,
  currentImageUrl,
  type,
  competenciaId,
  scope = 'competition' // 'competition' | 'user'
}) => {
  const { user } = useAuth();
  const userId = user?.uid;
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedImage, setSelectedImage] = useState(currentImageUrl);
  const [optimizing, setOptimizing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const loadImages = useCallback(async () => {
    if (!userId) return;
    if (scope !== 'user' && !competenciaId) return;

    setLoading(true);
    try {
      const folderName = (type === 'logo') ? 'logo' : (type === 'portada') ? 'covers' : (type === 'patrocinadores' ? 'patrocinadores' : 'otros');
      const baseFolder = scope === 'user'
        ? `usuarios/${userId}/logo`
        : `usuarios/${userId}/competencias/${competenciaId}/imagenes/${folderName}`;
      const folderRef = ref(storage, baseFolder);
      const listResult = await listAll(folderRef);
      
      const imagePromises = listResult.items
        .map(async (item) => {
          const url = await getDownloadURL(item);
          return {
            url,
            name: item.name,
            ref: item
          };
        });
      
      const loadedImages = await Promise.all(imagePromises);
      setImages(loadedImages);
    } catch (error) {
      console.error("Error cargando imágenes:", error);
      setSnackbar({
        open: true,
        message: 'Error al cargar imágenes',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [competenciaId, type, scope, userId]);

  useEffect(() => {
    if (open) {
      loadImages();
      setSelectedImage(currentImageUrl);
    }
  }, [open, loadImages, currentImageUrl]);

  const processFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) return;
    setOptimizing(true);
    try {
      const optimizationResult = await optimizeAndConvertToWebP(file);
      if (optimizationResult) {
        await uploadImage(optimizationResult.file);
      }
    } catch (error) {
      console.error("Error optimizando imagen:", error);
      setSnackbar({ open: true, message: 'Error al optimizar imagen', severity: 'error' });
    } finally {
      setOptimizing(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    await processFile(file);
  };

  const uploadImage = async (file) => {
    if (!userId) return;
    if (scope !== 'user' && !competenciaId) return;

    setUploading(true);
    setUploadProgress(0);
    
    const fileName = `${type}_${Date.now()}.webp`;
    const folderName = (scope === 'user') ? 'logo' : ((type === 'logo') ? 'logo' : (type === 'portada') ? 'covers' : (type === 'patrocinadores' ? 'patrocinadores' : 'otros'));
    const baseFolder = scope === 'user'
      ? `usuarios/${userId}/logo`
      : `usuarios/${userId}/competencias/${competenciaId}/imagenes/${folderName}`;
    const storageRef = ref(storage, `${baseFolder}/${fileName}`);
    
    try {
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          }
        );
      });
      
      await loadImages();
      setSnackbar({
        open: true,
        message: 'Imagen subida correctamente',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error subiendo imagen:", error);
      setSnackbar({
        open: true,
        message: 'Error al subir imagen',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (imageRef) => {
    setImageToDelete(imageRef);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!imageToDelete) return;
    
    setDeleting(true);
    try {
      await deleteObject(imageToDelete);
      
      setImages(prev => prev.filter(img => img.ref.toString() !== imageToDelete.toString()));
      
      if (selectedImage === imageToDelete.toString()) {
        setSelectedImage(null);
        onSelectImage(null);
      }
      
      setDeleteModalOpen(false);
      setSnackbar({
        open: true,
        message: 'Imagen eliminada correctamente',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error eliminando imagen:", error);
      setSnackbar({
        open: true,
        message: 'Error al eliminar imagen',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
      setImageToDelete(null);
    }
  };

  const handleSelect = () => {
    onSelectImage(selectedImage);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Gestionar {type === 'logo' ? 'Logo' : 'Imagen de Portada'}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box mb={2}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id={`upload-${type}`}
                  type="file"
                  onChange={handleFileChange}
                  disabled={uploading || optimizing}
                />
                <label htmlFor={`upload-${type}`}>
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      textAlign: 'center',
                      color: 'text.secondary',
                      cursor: 'pointer',
                      backgroundColor: 'background.default',
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                  >
                    <CloudUpload sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Arrastra y suelta una imagen o haz clic para seleccionar
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Se optimiza a WebP automáticamente
                    </Typography>
                  </Box>
                </label>
                {(uploading || optimizing) && (
                  <Box mt={2}>
                    <LinearProgress 
                      variant={optimizing ? 'indeterminate' : 'determinate'} 
                      value={uploadProgress} 
                    />
                    <Typography variant="caption" display="block" textAlign="center">
                      {optimizing ? 'Optimizando imagen...' : `Subiendo: ${Math.round(uploadProgress)}%`}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Grid container spacing={2}>
                {images.map((image) => (
                  <Grid item xs={12} sm={6} md={4} key={image.name}>
                    <Card sx={{ position: 'relative' }}>
                      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', bgcolor: 'grey.50' }}>
                        <Box component="img" src={image.url} alt={image.name} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      </Box>
                      <CardActions sx={{ justifyContent: 'space-between' }}>
                        <Tooltip title="Seleccionar esta imagen">
                          <IconButton 
                            onClick={() => setSelectedImage(image.url)}
                            color={selectedImage === image.url ? 'success' : 'default'}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar imagen">
                          <IconButton 
                            onClick={() => handleDeleteClick(image.ref)}
                            color="error"
                            disabled={deleting}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                      {selectedImage === image.url && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bgcolor: 'success.main',
                            color: 'white',
                            px: 1,
                            borderRadius: '0 4px 0 4px'
                          }}
                        >
                          <Typography variant="caption">Seleccionada</Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {images.length === 0 && !loading && (
                <Box textAlign="center" p={4}>
                  <PhotoCamera sx={{ fontSize: 60, color: 'text.disabled' }} />
                  <Typography variant="body1" color="textSecondary">
                    No hay imágenes {type === 'logo' ? 'de logo' : 'de portada'} cargadas
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button 
            onClick={handleSelect} 
            variant="contained" 
            disabled={!selectedImage || selectedImage === currentImageUrl}
          >
            Seleccionar
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={imageToDelete?.name || 'la imagen'}
        loading={deleting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ImageManagerModal;
